import {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Skeleton} from "@mui/material";
import {useLocation, useNavigate} from "react-router-dom";
import {Alert, Paper, styled, Typography, useTheme} from "@mui/material/";
import {Trans} from '@lingui/macro'
import useLogout from "../../hooks/useLogout";
import {getColumnsModel, getConfigOptions, mapObjectToLabels} from "../../pages/Statistics/utils";
import {DataGrid, gridClasses, GridToolbarContainer, GridToolbarExport} from "@mui/x-data-grid";
import useHttp from "../../hooks/useHttp";
import StatisticsFilterPanel from "./StatisticsFilterPanel";
import {StripedDataGrid} from "../tables/StripedDataGrid";

// eslint-disable-next-line no-restricted-globals
const allCrashesBaseUrl = new URL('/api/crash/all', location)
//
const paginationModel = {page: 0, pageSize: 25}

const TableSkeleton = () => {
    return ( // ? <CircularProgress sx={{ mt: 2 }} />
        <>
            <Skeleton width='100%' sx={{mt: 2, fontSize: '2rem'}}/>
            {Array.from({length: 5}).map((_, i) => <Skeleton key={i} width='100%'/>)}
            {/* <Skeleton width='100%' sx={{ fontSize: '2rem' }} /> */}
        </>
    )
}


const CrashList = ({isMobile, handleRowClick}) => {
    const navigate = useNavigate()
    const location = useLocation()
    const logout = useLogout()
    const state = location.state
    const token = state?.token || null
    const init = useMemo(() => state?.init || {}, [state?.init])
    const theme = useTheme()
    const drawerWidth = theme.mixins.drawerWidth
    const {isLoading, error, sendRequest} = useHttp()
    const [crashes, setCrashes] = useState([])
    const [totalCrashes, setTotalCrashes] = useState({})
    const [user, setUser] = useState({})
    const [rows, setRows] = useState([])
    const [formModel, setFormModel] = useState({})
    const [formList, setFormList] = useState([])
    const [message, setMessage] = useState('');
    const [columns, setColumns] = useState([])
    const [columnGroupingModel, setColumnGroupingModel] = useState([])

    const getCrashes = useCallback(data => {
        const allCrashesUrl = new URL(allCrashesBaseUrl)
        allCrashesUrl.searchParams.set('format', 'table')
        console.log(init);
        if (init.user.basic_organization === -1 && (init.user.type !== 1)) {
            allCrashesUrl.searchParams.append('merged', 1)
        }
        if (data) {
            Object.entries(data).forEach(([key, value]) => allCrashesUrl.searchParams.append(key, value))
        }
        sendRequest({
            token
            , url: allCrashesUrl
            , manageResData: res => {
                // Sorting and Adding id to each crash
                const sortedCrashes = res.crashes
                    // .filter(crash => crash['Crash id'] !== '20241002_00001')
                    .sort((a, b) => a['Crash id'] - b['Crash id'])
                    .map((crash, i) => ({...crash, id: i + 1}))
                setCrashes(sortedCrashes)
            }
        })
    }, [token, sendRequest, init])

    useEffect(() => {
        if (!token) return logout()
        if (isMobile) return navigate('/home', {state: {token, init}})
        getCrashes(null)
    }, [token, logout, isMobile, navigate, init, getCrashes])

    useEffect(() => {
        const {configuration, organizations, user, totalCrashes} = init;
        setUser(user);
        setTotalCrashes(totalCrashes);
        const configOptions = getConfigOptions(configuration, organizations)
        const mappedCrashes = crashes.map(crash => mapObjectToLabels(crash, configOptions))
        const preparedRows = mappedCrashes.map(crash => ({
            ...crash.road
            , ...crash.unit
            , ...crash.unit.people
            , ...crash.people
            , ...crash.crash
            , ...crash
        }))
        setRows(preparedRows)
        setFormModel(configuration)
        const initForms = Object.keys(configuration).sort((a, b) => configuration[a].position - configuration[b].position)
        setFormList(initForms)
        const {columns, columnGroupingModelWOrg: columnGroupingModel} = getColumnsModel(configuration)
        setColumns(columns)
        setColumnGroupingModel(columnGroupingModel)
    }, [init, crashes])

    console.log('gridClasses --- ',gridClasses)



    const groupedRows = useMemo(() => {
        if (!rows?.length) {
            return []
        }
        const groupBy = Object.groupBy(rows, item => item?.['Crash id'])
        const toArray = Object.entries(groupBy || {})?.map(([k, v], i) => ([k, i % 2 === 0]))
        const reduced = toArray?.reduce((acc, [k, v]) => {
            return ({...acc, ...{[k]: v}})
        }, {})
        return reduced
    }, [rows])

    const handleFilterSubmit = (searchParams) => {
        getCrashes(searchParams);
    }

    const handleFilterReset = () => {
        getCrashes(null);
    }

    const TopToolbar = ({totalCrashes}) => {
        return (
            <GridToolbarContainer sx={{justifyContent: 'space-between'}}>
                <GridToolbarExport/>
                <Typography align='right'>
                    <Trans>Crashes found</Trans>: {totalCrashes}
                </Typography>
            </GridToolbarContainer>
        )
    }


    return (
        <>
            <Box
                ml={`${drawerWidth}px`}

                pl={2}
                pr={2}
                display='flex'
                flexDirection='column'
                alignItems='stretch'

                sx={{
                    '& .overload-theme--header': {
                        backgroundColor: theme.mode === 'light' && theme.palette.primary.main
                        , color: theme.mode === 'light' && theme.palette.primary.contrastText
                    }
                }}
            >
                {/*
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    padding: 1.5
                }}>
                    <Typography sx={{fontSize: 18}} variant="h3">
                        <Trans>Crash list</Trans>
                    </Typography>
                    <Box sx={{alignSelf: 'end'}}>
                        <UserControls noElev={true} state={state}/>
                    </Box>
                </Box>

                <Divider flexItem/>
                */}
                {formList.length > 0 && (
                    <StatisticsFilterPanel
                        newLabel={'Crash list'}
                        title={"Crashes"}
                        formList={formList}
                        filterDisabled={false}
                        onFilterSubmit={handleFilterSubmit}
                        onFilterReset={handleFilterReset}
                        user={user}
                        formModel={formModel}
                        isMobile={isMobile}
                    />
                )}
                {isLoading
                    ? <TableSkeleton/>
                    : <Paper
                        sx={{mb: 2, display: 'flex', flexDirection: 'column', minHeight: '50vh'}}>
                        <StripedDataGrid
                            showColumnVerticalBorder
                            showCellVerticalBorder
                            unstable_rowSpanning
                            rows={rows}
                            onRowClick={params => {
                                handleRowClick(params)
                                navigate('/home', {state: {token, init}})
                            }}
                            columns={columns}
                            columnGroupingModel={columnGroupingModel}
                            initialState={{
                                pagination: {paginationModel}
                                , columns: {columnVisibilityModel: {id: false}}
                            }}
                            pageSizeOptions={[10, 25, 50]}
                            getRowClassName={params => {
                                return groupedRows?.[params?.row?.['Crash id']] ? 'even' : 'odd'
                            }}
                            slots={{
                                toolbar: TopToolbar
                            }}
                            slotProps={{
                                toolbar: {totalCrashes: totalCrashes.total}
                            }}
                        />
                    </Paper>
                }
            </Box>
            {error && <Alert sx={{mt: 2}} severity="error">
                <Trans>There was an error: {error}.</Trans>
            </Alert>}
            {message && <Alert sx={{mt: 2}} severity='success'>
                {message}
            </Alert>}
        </>
    )
}

export default CrashList